import React, { useState, useRef, useEffect, useCallback } from 'react';
import { fetchJobInfoSuggest, JobInfoSuggestResult } from '../../api/jobInfoApi';
import ShinyText from '../common/ShinyText/ShinyText';
import './JobRoleSearch.css';

interface JobRoleSearchProps {
  onRoleSelect: (roleId: string) => void;
  clearSignal?: number;
}

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 300;
const SEARCH_HINT = 'Search job roles and build with AI';

const JobRoleSearch: React.FC<JobRoleSearchProps> = ({ onRoleSelect, clearSignal = 0 }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<JobInfoSuggestResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setError(null);
    setSelectedIndex(-1);
  }, [clearSignal]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsOpen(false);
      setError(null);
      setIsLoading(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsLoading(true);
    setError(null);
    setIsOpen(true);

    try {
      const { data } = await fetchJobInfoSuggest(searchQuery);
      if (requestId !== requestIdRef.current) return;
      setResults(data.results);
      setSelectedIndex(-1);
    } catch {
      if (requestId !== requestIdRef.current) return;
      setResults([]);
      setError('Failed to load suggestions');
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < MIN_QUERY_LENGTH) {
      setResults([]);
      setIsOpen(false);
      setError(null);
      setIsLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      runSearch(query);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, runSearch]);

  const handleSelect = (result: JobInfoSuggestResult) => {
    onRoleSelect(result.role_id);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSelectedIndex(-1);
      return;
    }

    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && results[selectedIndex]) {
          handleSelect(results[selectedIndex]);
        }
        break;
      default:
        break;
    }
  };

  const highlightText = (text: string) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index}>{part}</mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className="job-role-search" ref={searchRef}>
      <div className="job-role-search__input-wrap">
        <img
          src="/assets/job-role-search-icon.png"
          alt=""
          className="job-role-search__icon"
          aria-hidden
        />
        <div className="job-role-search__input-field">
          {!query && (
            <span className="job-role-search__hint" aria-hidden="true">
              <ShinyText
                text={SEARCH_HINT}
                speed={3}
                color="#6b7280"
                shineColor="#a3e635"
                spread={120}
                direction="left"
                yoyo={false}
              />
            </span>
          )}
          <input
            ref={inputRef}
            type="text"
            className="job-role-search__input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => query.length >= MIN_QUERY_LENGTH && setIsOpen(true)}
            aria-label={SEARCH_HINT}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
          />
        </div>
        {query && (
          <button
            type="button"
            className="job-role-search__clear"
            onClick={() => {
              setQuery('');
              inputRef.current?.focus();
            }}
            aria-label="Clear search"
          >
            ×
          </button>
        )}
      </div>

      {isOpen && (
        <div className="job-role-search__dropdown" role="listbox">
          {isLoading ? (
            <div className="job-role-search__status">Searching...</div>
          ) : error ? (
            <div className="job-role-search__status job-role-search__status--error">{error}</div>
          ) : results.length > 0 ? (
            results.map((result, index) => (
              <button
                key={result.role_id}
                type="button"
                role="option"
                aria-selected={index === selectedIndex}
                className={`job-role-search__option ${index === selectedIndex ? 'job-role-search__option--selected' : ''}`}
                onClick={() => handleSelect(result)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <span className="job-role-search__option-name">
                  {highlightText(result.role_name)}
                </span>
                <span className="job-role-search__option-domain">
                  {highlightText(result.domain)}
                </span>
              </button>
            ))
          ) : (
            <div className="job-role-search__status">No roles found</div>
          )}
        </div>
      )}
    </div>
  );
};

export default JobRoleSearch;
